import { useState, useEffect } from 'react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import api from '../services/api';

interface LinkPreviewProps {
  url: string;
  onClose: () => void;
}

interface PreviewData {
  title?: string;
  description?: string;
  image?: string;
  favicon?: string;
  url: string;
  domain: string;
}

export default function LinkPreview({ url, onClose }: LinkPreviewProps) {
  const [copying, setCopying] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);

  console.log('🎨🎨🎨 LinkPreview component rendered! URL:', url);

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    // Fetch preview data
    fetchPreviewData();
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const fetchPreviewData = async () => {
    try {
      const response = await api.post('/link-preview', { url });
      setPreviewData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch preview:', error);
      // Fallback to basic data
      setPreviewData({
        url,
        domain: getDomain(url)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async () => {
    console.log('🔗🔗🔗 BUTTON CLICKED - Opening URL:', url);
    console.log('🔗 Is native platform:', Capacitor.isNativePlatform());
    console.log('🔗 Platform:', Capacitor.getPlatform());
    
    try {
      if (Capacitor.isNativePlatform()) {
        console.log('🔗 Using Browser.open()...');
        console.log('🔗 Browser object:', Browser);
        console.log('🔗 Browser.open function:', Browser.open);
        
        const result = await Browser.open({ url });
        console.log('✅ Browser.open() result:', result);
        console.log('✅ Browser.open() successful');
      } else {
        console.log('🔗 Using window.open()...');
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('❌ Error opening URL:', error);
      console.error('❌ Error details:', JSON.stringify(error));
      // Fallback: try window.open
      console.log('🔁 Trying fallback window.open...');
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    
    onClose();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopying(true);
      setTimeout(() => {
        setCopying(false);
        onClose();
      }, 1000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getDomain = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          animation: 'fadeIn 0.2s ease-out',
          padding: '1rem',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center'
        }}
      >
        {/* Bottom Sheet */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: '500px',
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '1rem',
            padding: '1.5rem',
            paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
            boxShadow: 'var(--shadow-xl)',
            animation: 'slideUp 0.3s ease-out',
            maxHeight: '80vh',
            overflow: 'auto'
          }}
        >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '1.5rem' }}>🔗</div>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', flex: 1 }}>
            Link-Vorschau
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.25rem',
              color: 'var(--gray-500)',
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </div>

        {/* URL Display */}
        <div style={{
          backgroundColor: 'var(--gray-50)',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem',
          border: '1px solid var(--gray-200)'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--gray-500)' }}>
              Lade Vorschau...
            </div>
          ) : (
            <>
              {previewData?.image && (
                <div style={{
                  width: '100%',
                  height: '200px',
                  borderRadius: '0.5rem',
                  overflow: 'hidden',
                  marginBottom: '1rem',
                  backgroundColor: 'var(--gray-200)'
                }}>
                  <img 
                    src={previewData.image} 
                    alt={previewData.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                {previewData?.favicon && (
                  <img 
                    src={previewData.favicon} 
                    alt=""
                    style={{ 
                      width: '20px', 
                      height: '20px',
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--primary)' }}>
                  {previewData?.domain || getDomain(url)}
                </div>
              </div>

              {previewData?.title && (
                <div style={{ 
                  fontSize: '1.125rem', 
                  fontWeight: '600', 
                  marginBottom: '0.5rem',
                  color: 'var(--gray-900)'
                }}>
                  {previewData.title}
                </div>
              )}

              {previewData?.description && (
                <div style={{ 
                  fontSize: '0.875rem', 
                  color: 'var(--gray-600)',
                  marginBottom: '0.75rem',
                  lineHeight: '1.4',
                  maxHeight: '3.6em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {previewData.description}
                </div>
              )}

              <div style={{ 
                fontSize: '0.75rem', 
                color: 'var(--gray-500)',
                wordBreak: 'break-all'
              }}>
                {url}
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <button
            onClick={(e) => {
              console.log('🔘🔘🔘 BUTTON ONCLICK FIRED!!!');
              console.log('🔘 Event:', e);
              e.preventDefault();
              e.stopPropagation();
              handleOpen();
            }}
            onTouchEnd={(e) => {
              console.log('👆👆👆 BUTTON TOUCHEND FIRED!!!');
              e.preventDefault();
              e.stopPropagation();
              handleOpen();
            }}
            className="btn"
            style={{
              backgroundColor: 'var(--primary)',
              color: 'white',
              padding: '1rem',
              fontSize: '1rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              minHeight: '48px',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)'
            }}
          >
            <span>🌐</span>
            Im Browser öffnen
          </button>

          <button
            onClick={handleCopy}
            className="btn"
            style={{
              backgroundColor: copying ? 'var(--success)' : 'var(--gray-100)',
              color: copying ? 'white' : 'var(--gray-700)',
              padding: '1rem',
              fontSize: '1rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              minHeight: '48px',
              transition: 'all 0.2s'
            }}
          >
            <span>{copying ? '✓' : '📋'}</span>
            {copying ? 'Kopiert!' : 'Link kopieren'}
          </button>

          <button
            onClick={onClose}
            className="btn"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--gray-600)',
              padding: '1rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: '1px solid var(--gray-300)',
              minHeight: '48px'
            }}
          >
            Abbrechen
          </button>
        </div>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from { 
              transform: translateY(100%);
              opacity: 0;
            }
            to { 
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </>
  );
}
